<template>
  <div
    class="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-cyan-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800 transition-colors duration-300">
    
    <!-- Header -->
    <header
      class="sticky top-0 z-40 bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 shadow-sm">
      <div class="container mx-auto px-4 sm:px-6 py-4">
        <div class="flex flex-col gap-5">
          <!-- Top Row: Title and Actions -->
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-4">
              <div class="relative group">
                <div
                  class="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg shadow-green-500/30 flex items-center justify-center text-white text-2xl transform group-hover:scale-105 transition-all duration-300">
                  <i class="fas fa-file-contract"></i>
                </div>
                <div class="absolute -right-2 -bottom-2 animate-bounce-small">
                  <span
                    class="flex h-5 w-5 items-center justify-center rounded-full bg-white dark:bg-dark-800 text-[10px] font-bold text-green-600 shadow-md ring-2 ring-white dark:ring-dark-800">
                    {{ totalItems }}
                  </span>
                </div>
              </div>
              <div>
                <p class="text-[11px] uppercase tracking-wider font-bold text-green-500 dark:text-green-400 mb-0.5">
                  Quotation Management</p>
                <h1
                  class="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-900 to-emerald-900 dark:from-green-100 dark:to-emerald-100">
                  Quotations
                </h1>
              </div>
            </div>

            <div class="flex items-center gap-3 flex-wrap">
              <!-- Search -->
              <div class="relative">
                <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input v-model="filters.search" type="text" placeholder="Search quotations..."
                  class="pl-9 pr-4 py-2 w-48 focus:w-64 transition-all duration-300 rounded-xl border-0 bg-white/50 dark:bg-dark-800/50 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 shadow-sm text-sm" />
                <button v-if="filters.search" @click="filters.search = ''"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <button v-if="hasPermission('quotations.create')" @click="openClientSelector"
                class="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 flex items-center justify-center gap-2 text-sm font-medium shadow-sm">
                <i class="fas fa-plus"></i>
                <span class="hidden sm:inline">New Quotation</span>
              </button>
            </div>
          </div>

          <!-- Quick Stats Cards -->
          <div class="flex flex-wrap gap-3">
            <div
              class="flex-1 min-w-[120px] bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm hover:transform hover:-translate-y-1 transition-all duration-300">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</p>
                <div
                  class="w-6 h-6 rounded-lg bg-gray-50 dark:bg-dark-700 flex items-center justify-center text-xs opacity-70">
                  <i class="fas fa-file-contract text-green-600"></i>
                </div>
              </div>
              <p class="text-xl font-black text-green-600">{{ totalItems }}</p>
            </div>
            <div
              class="flex-1 min-w-[120px] bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm hover:transform hover:-translate-y-1 transition-all duration-300">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">System Approved</p>
                <div
                  class="w-6 h-6 rounded-lg bg-gray-50 dark:bg-dark-700 flex items-center justify-center text-xs opacity-70">
                  <i class="fas fa-check-circle text-blue-500"></i>
                </div>
              </div>
              <p class="text-xl font-black text-blue-500">{{ systemApprovedCount }}</p>
            </div>
            <div
              class="flex-1 min-w-[120px] bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm hover:transform hover:-translate-y-1 transition-all duration-300">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Client Approved</p>
                <div
                  class="w-6 h-6 rounded-lg bg-gray-50 dark:bg-dark-700 flex items-center justify-center text-xs opacity-70">
                  <i class="fas fa-user-check text-emerald-500"></i>
                </div>
              </div>
              <p class="text-xl font-black text-emerald-500">{{ clientApprovedCount }}</p>
            </div>
            <div
              class="flex-1 min-w-[120px] bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm hover:transform hover:-translate-y-1 transition-all duration-300">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Client Approved Rate</p>
                <div
                  class="w-6 h-6 rounded-lg bg-gray-50 dark:bg-dark-700 flex items-center justify-center text-xs opacity-70">
                  <i class="fas fa-percentage text-indigo-500"></i>
                </div>
              </div>
              <p class="text-xl font-black text-indigo-500">{{ clientApprovedRate }}%</p>
            </div>
            <div
              class="flex-1 min-w-[120px] bg-white dark:bg-dark-800 p-3 rounded-xl border border-gray-200 dark:border-dark-700 shadow-sm hover:transform hover:-translate-y-1 transition-all duration-300">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Expired</p>
                <div
                  class="w-6 h-6 rounded-lg bg-gray-50 dark:bg-dark-700 flex items-center justify-center text-xs opacity-70">
                  <i class="fas fa-calendar-times text-orange-500"></i>
                </div>
              </div>
              <p class="text-xl font-black text-orange-500">{{ expiredCount }}</p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-4 sm:px-6 py-8">

    <!-- Filters -->
    <div class="bg-white/50 dark:bg-dark-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 p-3 sm:p-4 mb-4 sm:mb-6 ring-1 ring-gray-200 dark:ring-dark-700">
      <!-- Mobile: Stack filters vertically -->
      <div class="space-y-3 sm:hidden">
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Search</label>
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input type="text" v-model="filters.search" placeholder="Search by quotation #, client..." 
              class="pl-9 pr-4 py-2 w-full rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm"
              @input="debouncedFetchQuotations" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Status</label>
          <select v-model="filters.status" class="w-full px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="system_approved">System Approved</option>
            <option value="client_approved">Client Approved</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Date Range</label>
          <div class="grid grid-cols-2 gap-2">
            <input type="date" v-model="filters.start_date" class="px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations"
              placeholder="Start Date" />
            <input type="date" v-model="filters.end_date" class="px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations"
              placeholder="End Date" />
          </div>
        </div>
        <div class="flex justify-center">
          <button @click="resetFilters" class="w-full px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 border border-gray-300 dark:border-dark-600 rounded-xl bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
            Reset Filters
          </button>
        </div>
      </div>

      <!-- Desktop: Original horizontal layout -->
      <div class="hidden sm:grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Search</label>
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input type="text" v-model="filters.search" placeholder="Search by quotation #, client..." 
              class="pl-9 pr-4 py-2 w-full rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm"
              @input="debouncedFetchQuotations" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Status</label>
          <select v-model="filters.status" class="w-full px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="system_approved">System Approved</option>
            <option value="client_approved">Client Approved</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">Date Range</label>
          <div class="flex gap-2">
            <input type="date" v-model="filters.start_date" class="flex-1 px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations" />
            <input type="date" v-model="filters.end_date" class="flex-1 px-3 py-2 rounded-xl border-0 bg-white dark:bg-dark-800 ring-1 ring-gray-200 dark:ring-dark-700 focus:ring-2 focus:ring-green-500 text-sm" @change="fetchQuotations" />
          </div>
        </div>
        <div class="flex items-end">
          <button @click="resetFilters" class="w-full px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 border border-gray-300 dark:border-dark-600 rounded-xl bg-white dark:bg-dark-800 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
            Reset Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Quotations List -->
    <div class="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-700 overflow-hidden">
      <!-- Loading State -->
      <div v-if="loading" class="p-8 text-center text-sm text-gray-500 dark:text-dark-400">
        <div class="flex justify-center">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
        <div class="mt-2">Loading quotations...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="quotations.length === 0" class="p-8 text-center text-sm text-gray-500 dark:text-dark-400">
        No quotations found
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Mobile: Card Layout -->
        <div class="block lg:hidden">
          <div class="space-y-4 p-2">
            <div v-for="quotation in quotations" :key="quotation.id"
              class="bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-200 dark:border-dark-600 overflow-hidden hover:shadow-md hover:border-green-300 dark:hover:border-green-600 transition-all duration-300">
              <!-- Quotation Header -->
              <div
                class="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-dark-700 dark:to-dark-600 px-4 py-3 border-b border-gray-200 dark:border-dark-600">
                <div class="flex justify-between items-start">
                  <div class="flex items-center space-x-3">
                    <!-- Quotation ID Avatar -->
                    <div
                      class="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {{ quotation.quotation_number?.slice(-3) || '#' }}
                    </div>
                    <div class="flex-1">
                      <div class="font-semibold text-surface-900 dark:text-surface-100 text-sm">{{
                        quotation.quotation_number
                      }}</div>
                      <div class="text-xs text-surface-600 dark:text-surface-400">{{ formatDate(quotation.quote_date) }}
                      </div>
                    </div>
                  </div>
                  <div class="text-right">
                    <div class="font-bold text-surface-900 dark:text-surface-100 text-lg">{{
                      formatCurrency(quotation.total_amount) }}</div>
                  </div>
                </div>
              </div>

              <!-- Client Information -->
              <div class="px-4 py-3 border-b border-gray-200 dark:border-dark-600">
                <div class="flex items-center space-x-3">
                  <div class="w-8 h-8 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                    <i class="fas fa-user text-gray-600 dark:text-dark-400 text-sm"></i>
                  </div>
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 dark:text-dark-100 text-sm">{{ getClientDisplayName(quotation)
                    }}</div>
                    <div class="text-xs text-gray-500 dark:text-dark-400">Valid until: {{
                      formatDate(quotation.valid_until) }}</div>
                  </div>
                </div>
              </div>

              <!-- Quotation Status -->
              <div class="px-4 py-3 border-b border-gray-200 dark:border-dark-600">
                <div class="flex items-center justify-center">
                  <span :class="getStatusClass(quotation.status)" class="px-4 py-2 text-sm font-medium rounded-full">
                    {{ formatStatus(quotation.status) }}
                  </span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="px-4 py-3">
                <div class="flex flex-wrap gap-2">
                  <!-- Primary Actions -->
                  <button @click="viewQuotation(quotation)"
                    class="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="View Quotation">
                    <i class="fas fa-eye"></i>
                    <span class="hidden sm:inline">View</span>
                  </button>

                  <button v-if="hasPermission('quotations.edit') && quotation.status !== 'client_approved'"
                    @click="editQuotation(quotation)"
                    class="flex-1 bg-yellow-600 text-white px-3 py-2 rounded-lg hover:bg-yellow-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Edit Quotation">
                    <i class="fas fa-edit"></i>
                    <span class="hidden sm:inline">Edit</span>
                  </button>

                  <!-- PDF Generation -->
                  <button v-if="quotation.status === 'system_approved' || quotation.status === 'client_approved'"
                    @click="generatePDF(quotation)"
                    class="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Generate PDF">
                    <i class="fas fa-file-pdf"></i>
                    <span class="hidden sm:inline">PDF</span>
                  </button>

                  <!-- Approval Actions -->
                  <button v-if="hasPermission('quotations.approve') && quotation.status === 'pending'"
                    @click="approveQuotation(quotation)"
                    class="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Approve (System)">
                    <i class="fas fa-check"></i>
                    <span class="hidden sm:inline">Approve</span>
                  </button>

                  <button v-if="hasPermission('quotations.client_approve') && quotation.status === 'system_approved'"
                    @click="clientApproveQuotation(quotation)"
                    class="flex-1 bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Approve (Client)">
                    <i class="fas fa-user-check"></i>
                    <span class="hidden sm:inline">Client Approve</span>
                  </button>

                  <!-- Delete/Archive Actions -->
                  <button v-if="hasPermission('quotations.delete') && quotation.status === 'draft'"
                    @click="confirmDelete(quotation)"
                    class="flex-1 bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Delete Quotation">
                    <i class="fas fa-trash"></i>
                    <span class="hidden sm:inline">Delete</span>
                  </button>

                  <button v-if="hasPermission('quotations.delete') && quotation.status !== 'draft'"
                    @click="confirmSoftDelete(quotation)"
                    class="flex-1 bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 flex items-center justify-center gap-2 text-sm font-medium"
                    title="Archive Quotation">
                    <i class="fas fa-archive"></i>
                    <span class="hidden sm:inline">Archive</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: Table Layout -->
        <div class="hidden lg:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-surface-50/50 dark:bg-surface-800/50">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Quotation #
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Client
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Date
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Valid Until
                </th>
                <th
                  class="px-4 py-3 text-right text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Amount
                </th>
                <th
                  class="px-4 py-3 text-center text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Status
                </th>
                <th
                  class="px-4 py-3 text-center text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-surface-200 dark:divide-surface-700/50">
              <tr v-for="quotation in quotations" :key="quotation.id"
                class="hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors">
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm font-medium text-surface-900 dark:text-surface-100">{{ quotation.quotation_number
                  }}
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="text-sm text-gray-900 dark:text-dark-100">{{ getClientDisplayName(quotation) }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-500 dark:text-dark-400">{{ formatDate(quotation.quote_date) }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-500 dark:text-dark-400">{{ formatDate(quotation.valid_until) }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-right">
                  <div class="text-sm font-medium text-surface-900 dark:text-surface-100">{{
                    formatCurrency(quotation.total_amount) }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span :class="getStatusClass(quotation.status)"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-opacity-10">
                    {{ formatStatus(quotation.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <div class="flex justify-center space-x-2">
                    <button @click="viewQuotation(quotation)"
                      class="text-primary-600 hover:text-primary-800 dark:hover:text-primary-400 transition-colors"
                      title="View Quotation">
                      <i class="fas fa-eye"></i>
                    </button>
                    <button v-if="hasPermission('quotations.edit') && quotation.status !== 'client_approved'"
                      @click="() => { console.log('Edit button clicked for quotation:', quotation); editQuotation(quotation); }"
                      class="text-yellow-600 hover:text-yellow-800" title="Edit Quotation">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button v-if="hasPermission('quotations.approve') && quotation.status === 'pending'"
                      @click="approveQuotation(quotation)" class="text-blue-600 hover:text-blue-800"
                      title="Approve (System)">
                      <i class="fas fa-check"></i>
                    </button>
                    <button v-if="hasPermission('quotations.client_approve') && quotation.status === 'system_approved'"
                      @click="clientApproveQuotation(quotation)" class="text-green-600 hover:text-green-800"
                      title="Approve (Client)">
                      <i class="fas fa-user-check"></i>
                    </button>
                    <button v-if="quotation.status === 'system_approved' || quotation.status === 'client_approved'"
                      @click="generatePDF(quotation)" class="text-green-600 hover:text-green-800" title="Generate PDF">
                      <i class="fas fa-file-pdf"></i>
                    </button>
                    <button v-if="hasPermission('quotations.delete') && quotation.status === 'draft'"
                      @click="confirmDelete(quotation)"
                      class="text-error-600 hover:text-error-800 dark:hover:text-error-400 transition-colors"
                      title="Delete Quotation">
                      <i class="fas fa-trash"></i>
                    </button>
                    <button v-if="hasPermission('quotations.delete') && quotation.status !== 'draft'"
                      @click="confirmSoftDelete(quotation)" class="text-red-600 hover:text-red-800"
                      title="Archive Quotation">
                      <i class="fas fa-archive"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pagination -->
      <div
        class="bg-white dark:bg-dark-800 px-3 sm:px-4 lg:px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-dark-600">
        <!-- Mobile Pagination -->
        <div class="flex-1 flex justify-between sm:hidden">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1"
            class="relative inline-flex items-center px-3 py-2 border border-surface-300 dark:border-surface-600 text-sm font-medium rounded-md text-surface-700 dark:text-surface-300 bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
            :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }">
            ‹ Prev
          </button>
          <div class="flex items-center">
            <span class="text-sm text-surface-700 dark:text-surface-300">
              {{ currentPage }} / {{ totalPages }}
            </span>
          </div>
          <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages"
            class="relative inline-flex items-center px-3 py-2 border border-surface-300 dark:border-surface-600 text-sm font-medium rounded-md text-surface-700 dark:text-surface-300 bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
            :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }">
            Next ›
          </button>
        </div>

        <!-- Desktop Pagination -->
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p class="text-sm text-gray-700 dark:text-dark-300">
              Showing
              <span class="font-medium">{{ (currentPage - 1) * pageSize + 1 }}</span>
              to
              <span class="font-medium">{{ Math.min(currentPage * pageSize, totalItems) }}</span>
              of
              <span class="font-medium">{{ totalItems }}</span>
              results
            </p>
          </div>
          <div>
            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-surface-300 dark:border-surface-600 bg-surface-50 dark:bg-surface-800 text-sm font-medium text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }">
                <span class="sr-only">Previous</span>
                <i class="fas fa-chevron-left"></i>
              </button>
              <button v-for="page in paginationRange" :key="page" @click="changePage(page)" :class="[
                page === currentPage ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400' : 'bg-surface-50 dark:bg-surface-800 border-surface-300 dark:border-surface-600 text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700',
                'relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors'
              ]">
                {{ page }}
              </button>
              <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-surface-300 dark:border-surface-600 bg-surface-50 dark:bg-surface-800 text-sm font-medium text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }">
                <span class="sr-only">Next</span>
                <i class="fas fa-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
    </main>

    <!-- Client Selector Modal -->
    <Modal v-model="showClientSelector" size="5xl">
      <template #title>
        <div class="flex items-center gap-2">
          <i class="fas fa-user text-blue-600"></i>
          <span>Select Client for Quotation</span>
        </div>
      </template>

      <template #content>
        <div class="space-y-4">
          <!-- Search -->
          <div>
            <input type="text" v-model="clientSearch" placeholder="Search clients..." class="input-field"
              @input="debouncedFetchClients" />
          </div>

          <!-- Clients Table -->
          <div class="bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-300 dark:border-surface-600 overflow-hidden">
            <div class="overflow-x-auto max-h-96">
              <table class="min-w-full divide-y divide-surface-300 dark:divide-surface-600">
                <thead class="bg-surface-100 dark:bg-surface-700 sticky top-0">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Name
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">
                      Contact</th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Email
                    </th>
                    <th class="px-3 py-2 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">
                      Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-surface-50 dark:bg-surface-800 divide-y divide-surface-300 dark:divide-surface-600">
                  <template v-if="loadingClients">
                    <tr>
                      <td colspan="4" class="px-3 py-3 text-center text-sm text-surface-600 dark:text-surface-400">
                        Loading clients...
                      </td>
                    </tr>
                  </template>
                  <template v-else-if="clients.length === 0">
                    <tr>
                      <td colspan="4" class="px-3 py-3 text-center text-sm text-surface-600 dark:text-surface-400">
                        No clients found
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr v-for="client in clients" :key="client.id" class="hover:bg-surface-100 dark:hover:bg-surface-700">
                      <td class="px-3 py-2 text-sm text-surface-900 dark:text-surface-100">
                        {{ (client.display_name && client.display_name.trim()) || (client.company_name &&
                          client.company_name.trim()) || `${client.first_name || ''} ${client.last_name || ''}`.trim() }}
                      </td>
                      <td class="px-3 py-2 text-sm text-surface-600 dark:text-surface-400">{{ client.phone }}</td>
                      <td class="px-3 py-2 text-sm text-surface-600 dark:text-surface-400">{{ client.email }}</td>
                      <td class="px-3 py-2 text-center">
                        <button @click="selectClient(client)"
                          class="px-2 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                          Select
                        </button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end">
        <button @click="showClientSelector = false"
          class="px-4 py-2 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded-md hover:bg-surface-300 dark:hover:bg-surface-600 focus:outline-none focus:ring-2 focus:ring-surface-500">
          Cancel
        </button>
        </div>
      </template>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" size="md">
      <template #title>
        <div class="flex items-center gap-2 text-red-600">
          <i class="fas fa-exclamation-triangle"></i>
          <span>Confirm Delete</span>
        </div>
      </template>

      <template #content>
        <p class="text-gray-700 dark:text-dark-300">
          Are you sure you want to delete quotation <span class="font-semibold">{{ selectedQuotation?.quotation_number
          }}</span>?
          This action cannot be undone.
        </p>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <button @click="showDeleteModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-700 dark:text-dark-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500">
            Cancel
          </button>
          <button @click="deleteQuotation"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            :disabled="deleting">
            <i v-if="deleting" class="fas fa-spinner fa-spin mr-2"></i>
            Delete
          </button>
        </div>
      </template>
    </Modal>

    <!-- Quotation Create Modal -->
    <QuotationCreateModal v-if="selectedClient" v-model="showQuotationModal" :client="selectedClient"
      @created="fetchQuotations" />

    <!-- Quotation Preview Modal -->
    <QuotationPreviewModal v-if="selectedQuotationId" v-model="showPreviewModal" :quotation-id="selectedQuotationId" />

    <!-- Soft Delete Confirmation Modal -->
    <Modal v-model="showSoftDeleteModal">
      <template #title>
        <div class="flex items-center gap-2 text-red-600">
          <i class="fas fa-archive"></i>
          <span>Archive Quotation</span>
        </div>
      </template>

      <template #content>
        <p class="text-gray-700 dark:text-dark-300">
          Are you sure you want to archive quotation <span class="font-semibold">{{ selectedQuotation?.quotation_number
          }}</span>?
          This will hide it from the main list but it will still be accessible for audit purposes.
        </p>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <button @click="showSoftDeleteModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-700 dark:text-dark-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500">
            Cancel
          </button>
          <button @click="softDeleteQuotation"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            :disabled="deleting">
            <i v-if="deleting" class="fas fa-spinner fa-spin mr-2"></i>
            <span v-if="deleting">Archiving...</span>
            <span v-else>Archive</span>
          </button>
        </div>
      </template>
    </Modal>

    <!-- Approval Email Confirmation Modal -->
    <Modal v-model="showApprovalModal" size="md">
      <template #title>
        <div class="flex items-center gap-2 text-green-600">
          <i class="fas fa-check-circle"></i>
          <span>Approve Quotation</span>
        </div>
      </template>

      <template #content>
        <div class="space-y-4">
          <p class="text-gray-700 dark:text-dark-300">
            You are about to approve quotation <span class="font-semibold">{{ selectedQuotation?.quotation_number
            }}</span>.
          </p>
          <p class="text-gray-700 dark:text-dark-300">
            Would you like to send an email notification to the client?
          </p>
          <div class="flex items-center">
            <input type="checkbox" id="sendEmailCheckbox" v-model="sendEmailChoice"
              class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded">
            <label for="sendEmailCheckbox" class="ml-2 block text-sm text-gray-700 dark:text-dark-300">
              Send email notification to client
            </label>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <button @click="showApprovalModal = false"
            class="px-4 py-2 bg-gray-200 text-gray-700 dark:text-dark-300 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            :disabled="approving">
            Cancel
          </button>
          <button @click="confirmApprove"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            :disabled="approving">
            <i v-if="approving" class="fas fa-spinner fa-spin mr-2"></i>
            <span v-if="approving">Approving...</span>
            <span v-else>Approve</span>
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { format, parseISO } from 'date-fns';
import Modal from '@/components/common/Modal.vue';
import QuotationCreateModal from '../components/clients/QuotationCreateModal.vue';
import QuotationPreviewModal from '../components/clients/QuotationPreviewModal.vue';
import { useAuth } from '../composables/useAuth';
import { useMasterData } from '@/composables/useMasterData';
import debounce from 'lodash/debounce';
import { quotationsApi, clientsApi } from '@/services/api';
import { generateQuotationPDF } from '@/utils/clientPdfGenerator';

const toast = useToast();
const router = useRouter();
const { hasPermission } = useAuth();
const masterData = useMasterData();

// Reactive state
const quotations = ref([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

// Stats counts from API
const systemApprovedCount = ref(0);
const clientApprovedCount = ref(0);
const expiredCount = ref(0);

const filters = ref({
  search: '',
  status: '',
  start_date: '',
  end_date: '',
  sort_by: 'updated_at',
  sort_order: 'desc'
});

// Client selector
const showClientSelector = ref(false);
const clients = ref([]);
const loadingClients = ref(false);
const clientSearch = ref('');
const selectedClient = ref(null);
const showQuotationModal = ref(false);

// Preview modal
const showPreviewModal = ref(false);
const selectedQuotationId = ref(null);

// Delete confirmation
const showDeleteModal = ref(false);
const selectedQuotation = ref(null);
const deleting = ref(false);

// Soft delete confirmation
const showSoftDeleteModal = ref(false);

// Approval confirmation
const showApprovalModal = ref(false);
const sendEmailChoice = ref(true);
const approving = ref(false);

// Computed properties
const paginationRange = computed(() => {
  const range = [];
  const maxPages = 5;
  const startPage = Math.max(1, currentPage.value - Math.floor(maxPages / 2));
  const endPage = Math.min(totalPages.value, startPage + maxPages - 1);

  for (let i = startPage; i <= endPage; i++) {
    range.push(i);
  }

  return range;
});

// Fetch stats counts from API
const fetchStatsCounts = async () => {
  try {
    // Fetch system approved count
    const systemApprovedResponse = await quotationsApi.getAll({
      status: 'system_approved',
      limit: 1,
      page: 1
    });
    systemApprovedCount.value = systemApprovedResponse.data?.pagination?.total || 0;

    // Fetch client approved count
    const clientApprovedResponse = await quotationsApi.getAll({
      status: 'client_approved',
      limit: 1,
      page: 1
    });
    clientApprovedCount.value = clientApprovedResponse.data?.pagination?.total || 0;

    // Fetch expired count
    const expiredResponse = await quotationsApi.getAll({
      status: 'expired',
      limit: 1,
      page: 1
    });
    expiredCount.value = expiredResponse.data?.pagination?.total || 0;
  } catch (error) {
    console.error('Error fetching quotation stats counts:', error);
  }
};

// Stats computed properties
const clientApprovedRate = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.round((clientApprovedCount.value / totalItems.value) * 100);
});

// Methods
const fetchQuotations = async () => {
  loading.value = true;

  try {
    // Create a copy of filters to modify
    const apiFilters = { ...filters.value };

    // Special handling for deleted status
    if (apiFilters.status === 'deleted') {
      apiFilters.status = ''; // Clear the status filter
      apiFilters.is_deleted = true; // Set is_deleted flag to only show deleted quotations
    } else {
      // For other statuses, don't include deleted quotations
      apiFilters.is_deleted = false;
    }

    const response = await quotationsApi.getAll({
      page: currentPage.value,
      limit: pageSize.value,
      ...apiFilters
    });

    // For deleted quotations, set their status to "deleted" for display purposes
    if (apiFilters.is_deleted) {
      quotations.value = response.data.data.map(quotation => ({
        ...quotation,
        status: 'deleted'
      }));
    } else {
      quotations.value = response.data.data;
    }

    totalItems.value = response.data.pagination.total;
    totalPages.value = response.data.pagination.total_pages;

    // Adjust current page if it's out of range
    if (currentPage.value > totalPages.value && totalPages.value > 0) {
      currentPage.value = totalPages.value;
      fetchQuotations();
    }
  } catch (error) {
    console.error('Error fetching quotations:', error);
    toast.error('Failed to load quotations');
  } finally {
    loading.value = false;
  }
};

const debouncedFetchQuotations = debounce(fetchQuotations, 300);

const fetchClients = async () => {
  loadingClients.value = true;

  try {
    const result = await masterData.loadClients({
      search: clientSearch.value,
      limit: 100
    });

    // Handle paginated response structure
    if (result && typeof result === 'object' && result.data && Array.isArray(result.data)) {
      clients.value = result.data;
    } else if (Array.isArray(result)) {
      clients.value = result;
    } else {
      clients.value = [];
    }

    console.log('Clients loaded for selector:', clients.value.length);
  } catch (error) {
    console.error('Error fetching clients:', error);
    toast.error('Failed to load clients');
  } finally {
    loadingClients.value = false;
  }
};

const debouncedFetchClients = debounce(fetchClients, 300);

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchQuotations();
};

const resetFilters = () => {
  filters.value = {
    search: '',
    status: '',
    start_date: '',
    end_date: '',
    sort_by: 'updated_at',
    sort_order: 'desc'
  };
  fetchQuotations();
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return format(parseISO(dateString), 'MMMM d, yyyy');
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP'
  }).format(value || 0);
};

const formatStatus = (status) => {
  if (!status) return '';

  // Custom formatting for multi-word statuses
  if (status === 'system_approved') return 'System Approved';
  if (status === 'client_approved') return 'Client Approved';

  // Default formatting for single word statuses
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getStatusClass = (status) => {
  const statusClasses = {
    'draft': 'bg-surface-100 text-surface-800 dark:text-surface-200',
    'pending': 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300',
    'system_approved': 'bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-300',
    'client_approved': 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300',
    'rejected': 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300',
    'expired': 'bg-secondary-100 dark:bg-secondary-900/30 text-secondary-800 dark:text-secondary-300',
    'deleted': 'bg-error-50 dark:bg-error-900/10 text-error-600 dark:text-error-400'
  };

  return statusClasses[status] || 'bg-surface-100 text-surface-800 dark:text-surface-200';
};

const getClientDisplayName = (quotation) => {
  // Prefer explicit display_name when available
  if (quotation.display_name && typeof quotation.display_name === 'string' && quotation.display_name.trim()) {
    return quotation.display_name.trim();
  }

  // Next prefer company name
  if (quotation.company_name && typeof quotation.company_name === 'string' && quotation.company_name.trim()) {
    return quotation.company_name.trim();
  }

  // Use client_name if provided by API (already composed)
  if (quotation.client_name && typeof quotation.client_name === 'string' && quotation.client_name.trim()) {
    return quotation.client_name.trim();
  }

  // Construct from first and last name when available
  const first = typeof quotation.first_name === 'string' ? quotation.first_name.trim() : '';
  const last = typeof quotation.last_name === 'string' ? quotation.last_name.trim() : '';
  const full = `${first} ${last}`.trim();
  if (full) return full;

  // Fallback
  return 'Unknown Client';
};

const openClientSelector = () => {
  showClientSelector.value = true;
  clientSearch.value = '';
  fetchClients();
};

const selectClient = (client) => {
  selectedClient.value = client;
  showClientSelector.value = false;
  showQuotationModal.value = true;
};

const viewQuotation = (quotation) => {
  // Open the preview modal instead of navigating
  selectedQuotationId.value = quotation.id;
  showPreviewModal.value = true;
};

const editQuotation = (quotation) => {
  // Navigate to quotation edit page
  router.push(`/quotations/${quotation.id}/edit`);
};

const generatePDF = async (quotation) => {
  try {
    // Add a loading toast that can be updated
    const toastId = toast.info('Generating PDF...', {
      timeout: false,
      closeButton: false
    });

    // First, we need to get the full quotation data with items if not already available
    let fullQuotation = quotation;
    if (!quotation.items) {
      try {
        const response = await quotationsApi.getById(quotation.id);
        fullQuotation = response.data.data;
      } catch (error) {
        console.error('Error fetching quotation details:', error);
        throw new Error('Failed to fetch quotation details for PDF generation');
      }
    }

    // Generate PDF on the client side
    console.log('Generating client-side PDF for quotation:', fullQuotation.quotation_number);
    const pdfBlob = await generateQuotationPDF(fullQuotation);

    // Create a URL for the blob
    const url = window.URL.createObjectURL(pdfBlob);

    // Close the loading toast
    toast.dismiss(toastId);
    toast.success('PDF generated successfully');

    // Create a temporary link element to force download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Quotation-${fullQuotation.quotation_number}.pdf`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 1000);
  } catch (err) {
    console.error('Error generating PDF:', err);
    toast.error(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
  }
};

const confirmDelete = (quotation) => {
  selectedQuotation.value = quotation;
  showDeleteModal.value = true;
};

const deleteQuotation = async () => {
  if (!selectedQuotation.value) return;

  deleting.value = true;

  try {
    await quotationsApi.delete(selectedQuotation.value.id);

    toast.success('Quotation deleted successfully');
    showDeleteModal.value = false;
    fetchQuotations();
  } catch (error) {
    console.error('Error deleting quotation:', error);
    toast.error(`Failed to delete quotation: ${error.response?.data?.message || error.message}`);
  } finally {
    deleting.value = false;
  }
};

const confirmSoftDelete = (quotation) => {
  selectedQuotation.value = quotation;
  showSoftDeleteModal.value = true;
};

const approveQuotation = (quotation) => {
  selectedQuotation.value = quotation;
  sendEmailChoice.value = true; // Default to checked
  showApprovalModal.value = true;
};

const confirmApprove = async () => {
  if (!selectedQuotation.value) return;

  approving.value = true;
  try {
    await quotationsApi.approve(selectedQuotation.value.id, {
      sendEmail: sendEmailChoice.value
    });
    const emailMessage = sendEmailChoice.value
      ? `Quotation ${selectedQuotation.value.quotation_number} has been system approved and email sent`
      : `Quotation ${selectedQuotation.value.quotation_number} has been system approved (no email sent)`;
    toast.success(emailMessage);
    showApprovalModal.value = false;
    fetchQuotations();
  } catch (error) {
    console.error('Error approving quotation:', error);
    toast.error(`Failed to approve quotation: ${error.response?.data?.message || error.message}`);
  } finally {
    approving.value = false;
  }
};

const clientApproveQuotation = async (quotation) => {
  try {
    await quotationsApi.clientApprove(quotation.id);
    toast.success(`Quotation ${quotation.quotation_number} has been client approved`);
    fetchQuotations();
  } catch (error) {
    console.error('Error client approving quotation:', error);
    toast.error(`Failed to client approve quotation: ${error.response?.data?.message || error.message}`);
  }
};

const softDeleteQuotation = async () => {
  if (!selectedQuotation.value) return;

  deleting.value = true;

  try {
    await quotationsApi.delete(`${selectedQuotation.value.id}/soft`);

    toast.success('Quotation archived successfully');
    showSoftDeleteModal.value = false;
    fetchQuotations();
  } catch (error) {
    console.error('Error archiving quotation:', error);
    toast.error(`Failed to archive quotation: ${error.response?.data?.message || error.message}`);
  } finally {
    deleting.value = false;
  }
};

// Lifecycle hooks
onMounted(() => {
  fetchQuotations();
  fetchStatsCounts();
});
</script>

